import React from 'react';
import { Card } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js';
import PropTypes from 'prop-types';

// Register Chart.js modules
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const StatCard = ({ title, value, subtitle, sparklineData, breakdownData, emptyMessage }) => {
  // Format value as currency
  const formatCurrency = (val) => {
    if (isNaN(val)) return '0 kr';
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' })
      .format(val)
      .replace('kr', '')
      .trim();
  };

  // Prepare sparkline data
  const chartData = {
    labels: sparklineData ? sparklineData.map((_, i) => i) : [],
    datasets: [
      {
        data: sparklineData ? sparklineData.map(item => item.value) : [],
        borderColor: '#003D7A',
        backgroundColor: 'rgba(0, 61, 122, 0.08)',
        borderWidth: 2.5,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointBackgroundColor: '#003D7A',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        clip: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 61, 122, 0.9)',
        padding: 10,
        borderRadius: 6,
        titleFont: { size: 13, weight: '600' },
        bodyFont: { size: 12 },
        displayColors: false,
        callbacks: {
          label: (context) => {
            const val = context.parsed.y;
            return formatCurrency(val);
          },
        },
      },
    },
    scales: {
      x: {
        display: false,
        grid: {
          display: false,
        },
      },
      y: {
        display: false,
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <Card className="stat-card border-0" style={{ borderRadius: '12px' }}>
      <Card.Body style={{ padding: '1.5rem' }}>
        <h6 style={{
          fontSize: '0.75rem',
          fontWeight: '600',
          color: '#6c757d',
          marginBottom: '0.5rem',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
        }}>
          {title}
        </h6>
        <h2 style={{
          fontSize: '3.5rem',
          fontWeight: '700',
          color: '#003D7A',
          marginBottom: '0.75rem',
          lineHeight: '1.1',
        }}>
          {formatCurrency(value)} kr
        </h2>
        {breakdownData && breakdownData.length > 0 ? (
          <div style={{
            marginBottom: '1rem',
            marginLeft: '-1.5rem',
            marginRight: '-1.5rem',
            paddingLeft: '1.5rem',
            paddingRight: '1.5rem',
          }}>
            {/* Stacked Progress Bar */}
            <div style={{
              display: 'flex',
              height: '40px',
              borderRadius: '6px',
              overflow: 'hidden',
              backgroundColor: '#F0F2F5',
              marginBottom: '0.75rem',
              boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
            }}>
              {breakdownData.map((item, index) => (
                <div
                  key={index}
                  style={{
                    width: `${item.percentage}%`,
                    backgroundColor: item.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '0.7rem',
                    fontWeight: '700',
                    transition: 'width 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRight: index < breakdownData.length - 1 ? '2px solid rgba(255,255,255,0.3)' : 'none',
                    padding: '0 4px',
                  }}
                  title={`${item.name}: ${formatCurrency(item.value)} kr`}
                >
                  {item.percentage > 10 && (
                    <span style={{ position: 'relative', zIndex: 1 }}>
                      {formatCurrency(item.value)} kr
                    </span>
                  )}
                </div>
              ))}
            </div>
            {/* Legend */}
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              {breakdownData.map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '2px',
                      backgroundColor: item.color,
                    }}
                  />
                  <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                    {item.name}: <strong>{formatCurrency(item.value)} kr</strong>
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : sparklineData && sparklineData.length > 0 && (
          <div style={{
            height: '80px',
            marginBottom: '1rem',
            marginLeft: '-1.5rem',
            marginRight: '-1.5rem',
            paddingLeft: '0',
            paddingRight: '0',
          }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        )}
        {value === 0 && emptyMessage ? (
          <div style={{
            backgroundColor: '#FFF3CD',
            padding: '1rem',
            borderRadius: '6px',
            borderLeft: '4px solid #FFC107',
            marginTop: '0.5rem',
          }}>
            <p style={{
              fontSize: '0.85rem',
              color: '#664D03',
              margin: 0,
              lineHeight: '1.5',
              fontWeight: '600',
            }}>
              {emptyMessage}
            </p>
          </div>
        ) : (
          <small style={{
            fontSize: '0.8rem',
            color: '#95a3b3',
            fontWeight: '500',
            display: 'block',
            lineHeight: '1.4',
          }}>
            {subtitle}
          </small>
        )}
      </Card.Body>
    </Card>
  );
};

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  subtitle: PropTypes.string,
  sparklineData: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
    })
  ),
  breakdownData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      percentage: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    })
  ),
  emptyMessage: PropTypes.string,
};

StatCard.defaultProps = {
  subtitle: '',
  sparklineData: [],
  breakdownData: null,
  emptyMessage: null,
};

export default StatCard;
